import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  DeliveryService,
  DeliveryType,
  PaymentMethod,
} from "../../../common/types/enums";
import { getApi } from "../../../api/api";
import { selectClusterList } from "../../../store/slices/clusterListSlice";
import { selectCity } from "../../../store/slices/citySlice";
import { selectCurrentUser } from "../../../store/slices/currentUserSlice";
import { setCart } from "../../../store/slices/cartSlice";

import { useCartData } from "./useCartData";
import { useAddressManagement } from "./useAddressManagement";
import { useCartStoreCheck } from "./useCartStoreCheck";
import { useDeliveryCalculator } from "./useDeliveryCalculator";
import { usePromoManagement } from "./usePromoManagement";
import { useBonuses } from "./useBonuses";
import { useOrderCreation } from "./useOrderCreation";
import { useDeliveryTime } from "./useDeliveryTime";
import { useOrderDetails } from "./useOrderDetails";
import { useCartValidation } from "./useCartValidation";
import { useCdekDelivery } from "./useCdekDelivery";

export const CartModalStage = {
  CHECKOUT: "CHECKOUT",
  ORDER_SUCCESS: "ORDER_SUCCESS",
} as const;
export type CartModalStageType =
  (typeof CartModalStage)[keyof typeof CartModalStage];

export const useCartCheckout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const api = getApi();
  const [searchParams] = useSearchParams();

  const clusterId = Number(searchParams.get("clusterId") || 0);
  const clusters = useSelector(selectClusterList);
  const selectedCity = useSelector(selectCity);
  const currentUser = useSelector(selectCurrentUser);
  const currentCluster = clusters.find((c) => Number(c.id) === clusterId);

  const [stage, setStage] = useState<CartModalStageType>(
    CartModalStage.CHECKOUT,
  );
  const [deliveryType, setDeliveryType] = useState<DeliveryType>(
    DeliveryType.UNSET,
  );
  const [pickUpStore, setPickUpStore] = useState<number | undefined>(undefined);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    PaymentMethod.UNSET,
  );
  const [orderNumber, setOrderNumber] = useState<number | null>(null);
  const [orderDisabled, setOrderDisabled] = useState<boolean>(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isOrderCreating, setIsOrderCreating] = useState(false);
  const [storeCheckResult, setStoreCheckResult] = useState<any | null>(null);
  const [selectedGiftItemId, setSelectedGiftItemId] = useState<
    number | undefined
  >(undefined);

  const { cart, saleItemList, storeList } = useCartData(clusterId);

  const address = useAddressManagement();
  const bonuses = useBonuses(clusterId);
  const stockCheck = useCartStoreCheck(cart.items, saleItemList, clusterId);

  const {
    phone,
    comment,
    getValidationResult,
    inputHandler,
    setDirty,
    isValid: orderDetailsValid,
    MAX_COMMENT_LENGTH,
    cutleryCount,
    incrementCutlery,
    decrementCutlery,
  } = useOrderDetails();

  const itemsForOrder = storeCheckResult?.itemsForOrder || cart.items;
  const itemsRemaining = storeCheckResult?.itemsRemaining || [];

  const {
    available: deliveryAvailable,
    deliveryStoreId,
    deliveryDistance,
    deliveryFee: calculatedDeliveryFee,
    loading: deliveryLoading,
    error: deliveryError,
    applicableRules,
  } = useDeliveryCalculator({
    cartItems: itemsForOrder,
    deliveryAddress: address.selectedAddress
      ? {
          latitude: Number(address.selectedAddress.address?.latitude),
          longitude: Number(address.selectedAddress.address?.longitude),
        }
      : null,
    deliveryType,
    clusterId,
  });

  const {
    options: cdekOptions,
    loading: cdekLoading,
    error: cdekError,
    selectedTariff,
    selectedPickupPoint,
    setSelectedTariff,
    setSelectedPickupPoint,
    deliveryService,
    setDeliveryService,
    setCdekTariffCode,
    applyMarkup,
  } = useCdekDelivery({
    deliveryType,
    storeId: deliveryStoreId || pickUpStore || null,
    cityId: address.selectedAddress?.city?.id || selectedCity?.id || null,
    addressId: address.selectedAddressId || null,
    clusterId,
  });

  useEffect(() => {
    if (deliveryStoreId) {
      const result = stockCheck.checkStore(deliveryStoreId);
      setStoreCheckResult(result);
    }
  }, [deliveryStoreId]);

  const {
    desiredTime,
    displayText: deliveryTimeDisplay,
    pickerVisible: deliveryTimePickerVisible,
    openPicker: openDeliveryTimePicker,
    closePicker: closeDeliveryTimePicker,
    handleTimeSelect: onDeliveryTimeSelect,
    minimumDate: deliveryTimeMinimumDate,
    isTimeAvailable,
  } = useDeliveryTime({
    deliveryType,
    selectedStore: storeList.find((s) => s.id === pickUpStore),
    clusterId,
  });

  const promo = usePromoManagement({
    cartItems: itemsForOrder,
    deliveryType,
    pickUpStore,
    clusterId,
    onGiftAutoSelect: setSelectedGiftItemId,
  });

  const {
    calculateItemsTotal,
    calculateMaxWriteOff,
    calculateEarnedBonuses,
    getNextBonusLevelText,
    getBestDeliveryRuleText,
  } = useCartValidation();

  const deliveryFee =
    deliveryService === DeliveryService.CDEK && selectedTariff
      ? applyMarkup(selectedTariff.delivery_sum)
      : calculatedDeliveryFee;

  const itemsTotal = calculateItemsTotal(itemsForOrder);
  const maxWriteOff = calculateMaxWriteOff(
    itemsTotal,
    bonuses.balance,
    bonuses.writeOffPercent,
  );

  const discountAmount = promo.selectedPromo
    ? itemsTotal * (promo.selectedPromo.discount / 100)
    : 0;

  const totalWithDelivery =
    itemsTotal - discountAmount - bonuses.bonusesToSpend + deliveryFee;

  const { createOrder: createOrderBase } = useOrderCreation({
    cluster: currentCluster,
    cartItems: itemsForOrder,
    saleItemList,
    itemsTotal,
    deliveryFee,
    discountAmount,
    totalWithDelivery,
    deliveryType,
    pickUpStore,
    deliveryStoreId,
    selectedAddressId: address.selectedAddressId,
    paymentMethod,
    selectedPromo: promo.selectedPromo,
    discountCode: promo.discountCode,
    bonusesToSpend: bonuses.bonusesToSpend,
    desiredTime: desiredTime ?? deliveryTimeMinimumDate?.toISOString() ?? null,
    phone,
    comment,
    cutleryCount,
    deliveryService,
    selectedGiftItemId,
    cdekOrder:
      deliveryService === DeliveryService.CDEK && selectedTariff
        ? {
            tariffCode: selectedTariff.tariff_code,
            pickupPointCode: selectedPickupPoint?.code,
            pickupPointName: selectedPickupPoint?.name,
            pickupPointAddress:
              selectedPickupPoint?.location?.address_full ||
              selectedPickupPoint?.address,
            pickupPointPhone: selectedPickupPoint?.phones?.[0]?.number,
            tariffName: selectedTariff.tariff_name,
            periodMin: selectedTariff.period_min,
            periodMax: selectedTariff.period_max,
          }
        : undefined,
  });

  const handleCreateOrder = useCallback(async () => {
    if (isOrderCreating) return;
    setIsOrderCreating(true);
    try {
      setValidationError(null);
      const result = await createOrderBase();
      if (result?.id) {
        setOrderNumber(result.id);
        setStage(CartModalStage.ORDER_SUCCESS);
        if (itemsRemaining.length > 0) {
          dispatch(setCart(itemsRemaining));
        }
      }
    } catch (error: any) {
      setValidationError(
        error?.response?.data?.message || "Не удалось создать заказ",
      );
    } finally {
      setIsOrderCreating(false);
    }
  }, [isOrderCreating, createOrderBase, itemsRemaining, dispatch]);

  const handleDeliveryTypeChange = useCallback(
    (type: DeliveryType) => {
      setDeliveryType(type);
      if (type !== DeliveryType.PICKUP) {
        promo.setSelectedPromo(null);
        promo.setDiscountCode("");
        setStoreCheckResult(null);
      }
      if (type === DeliveryType.PICKUP) {
        address.setSelectedAddressId(null);
      }
    },
    [promo, address],
  );

  const handlePickUpStoreChange = useCallback(
    (id: number) => {
      setPickUpStore(id);
      const result = stockCheck.checkStore(id);
      setStoreCheckResult(result);
    },
    [stockCheck],
  );

  const handleAddressSelect = useCallback(
    (addressId: number) => address.setSelectedAddressId(addressId),
    [address],
  );

  const handlePaymentMethodChange = useCallback(
    (method: PaymentMethod) => setPaymentMethod(method),
    [],
  );

  useEffect(() => {
    if (stage === CartModalStage.CHECKOUT) {
      address.loadAddresses();
      bonuses.loadBonuses();
    }
  }, [stage]);

  useEffect(() => {
    const payment = currentCluster?.payment;
    const isDelivery = deliveryType === DeliveryType.DELIVERY;
    const hasPaymentMethods =
      (isDelivery &&
        (payment?.cashOnDelivery ||
          payment?.cardOnDelivery ||
          payment?.onlineOnDelivery)) ||
      (!isDelivery &&
        (payment?.cashOnPickup ||
          payment?.cardOnPickup ||
          payment?.onlineOnPickup));

    let timeValidationError = null;
    if (desiredTime) {
      const selectedDate = new Date(desiredTime);
      if (!isTimeAvailable(selectedDate)) {
        timeValidationError =
          "Выбранное время недоступно. Пожалуйста, выберите другое время.";
      }
    }

    const disabled =
      !orderDetailsValid ||
      (!pickUpStore && deliveryType === DeliveryType.PICKUP) ||
      (hasPaymentMethods && paymentMethod === PaymentMethod.UNSET) ||
      deliveryType === DeliveryType.UNSET ||
      (deliveryType === DeliveryType.DELIVERY && !address.selectedAddressId) ||
      (deliveryType === DeliveryType.DELIVERY &&
        deliveryService !== DeliveryService.CDEK &&
        !deliveryAvailable) ||
      (deliveryType === DeliveryType.DELIVERY &&
        deliveryService !== DeliveryService.CDEK &&
        !deliveryStoreId) ||
      (deliveryType === DeliveryType.DELIVERY &&
        deliveryService === DeliveryService.CDEK &&
        !selectedTariff) ||
      (promo.selectedPromo && !promo.validatePromo()) ||
      !!timeValidationError;

    setValidationError(timeValidationError);
    setOrderDisabled(Boolean(disabled));
  }, [
    orderDetailsValid,
    deliveryType,
    paymentMethod,
    pickUpStore,
    address.selectedAddressId,
    deliveryAvailable,
    deliveryStoreId,
    promo,
    currentCluster,
    desiredTime,
    isTimeAvailable,
    deliveryService,
    selectedTariff,
  ]);

  return {
    cluster: currentCluster,
    clusterId,
    stage,
    setStage,
    deliveryType,
    selectedAddressId: address.selectedAddressId,
    selectedAddress: address.selectedAddress,
    addresses: address.addresses,
    paymentMethod,
    orderNumber,
    orderDisabled,
    isOrderCreating,
    validationError,

    deliveryAvailable,
    deliveryStoreId,
    deliveryDistance,
    deliveryLoading,
    deliveryError,

    desiredTime,
    deliveryTimeDisplay,
    deliveryTimePickerVisible,
    openDeliveryTimePicker,
    closeDeliveryTimePicker,
    onDeliveryTimeSelect,
    deliveryTimeMinimumDate,

    itemsForOrder,
    itemsRemaining,
    storeCheckResult,

    itemsTotal,
    deliveryFee,
    discountAmount,
    totalWithDelivery,

    selectedPromo: promo.selectedPromo,
    discountCode: promo.discountCode,
    applicablePromos: promo.applicablePromos,

    bonusBalance: bonuses.balance,
    bonusTotalSpent: bonuses.totalSpent,
    bonusPercent: bonuses.bonusPercent,
    bonusesToSpend: bonuses.bonusesToSpend,
    maxWriteOff,
    earnedBonuses: calculateEarnedBonuses(itemsTotal, bonuses.bonusPercent, 0),
    nextLevelText: getNextBonusLevelText(
      bonuses.totalSpent,
      bonuses.bonusRules,
    ),

    storeList,
    pickUpStore,

    phone,
    comment,
    getValidationResult,
    inputHandler,
    setDirty,
    orderDetailsValid,
    MAX_COMMENT_LENGTH,

    loadAddresses: address.loadAddresses,
    createAddress: address.createAddress,
    createOrder: handleCreateOrder,
    handleDeliveryTypeChange,
    handleAddressSelect,
    handlePaymentMethodChange,
    handlePickUpStoreChange,
    handleSelectPromo: promo.setSelectedPromo,
    setDiscountCode: promo.setDiscountCode,
    validatePromoBeforeOrder: promo.validatePromo,
    setBonusesToSpend: bonuses.setBonusesToSpend,
    loadBonuses: bonuses.loadBonuses,
    getBestDeliveryRuleText,

    isTimeAvailable,
    cdekOptions,
    cdekLoading,
    cdekError,
    selectedTariff,
    selectedPickupPoint,
    setSelectedTariff,
    setSelectedPickupPoint,
    setDeliveryService,
    setCdekTariffCode,
    deliveryService,

    cutleryCount,
    incrementCutlery,
    decrementCutlery,
    applicableRules,

    selectedGiftItemId,
    setSelectedGiftItemId,

    cart,
    saleItemList,
    currentUser,
    navigate,
  };
};
